import React from 'react';
import { motion } from 'framer-motion';
import { ChevronUp } from 'lucide-react';

const Footer: React.FC = () => {
    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const currentYear = new Date().getFullYear();

    return (
        <footer className="relative overflow-hidden">
            {/* Smooth Gradient Transition from Contact Section */}
            <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white via-slate-50/80 to-slate-100/60" />

            {/* Main Footer with Liquid Glass Effect */}
            <div className="relative bg-gradient-to-b from-slate-100/60 via-slate-800/95 to-slate-900">
                {/* Liquid Glass Background Effects */}
                <div className="absolute inset-0 overflow-hidden">
                    {/* Floating Glass Orbs */}
                    <motion.div
                        className="absolute top-20 left-1/4 w-64 h-64"
                        animate={{
                            scale: [1, 1.1, 1],
                            rotate: [0, 180, 360],
                            opacity: [0.1, 0.2, 0.1],
                        }}
                        transition={{
                            duration: 25,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                        style={{
                            background: 'radial-gradient(circle at 30% 30%, rgba(14, 165, 233, 0.15) 0%, rgba(139, 92, 246, 0.08) 50%, transparent 80%)',
                            borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
                            filter: 'blur(40px)',
                            backdropFilter: 'blur(10px)',
                        }}
                    />

                    <motion.div
                        className="absolute bottom-10 right-1/3 w-48 h-48"
                        animate={{
                            scale: [1, 0.9, 1.2, 1],
                            rotate: [360, 180, 0],
                            opacity: [0.08, 0.15, 0.08],
                        }}
                        transition={{
                            duration: 30,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                        style={{
                            background: 'radial-gradient(circle at 70% 40%, rgba(139, 92, 246, 0.12) 0%, rgba(6, 182, 212, 0.06) 60%, transparent 85%)',
                            borderRadius: '40% 60% 70% 30% / 40% 70% 60% 30%',
                            filter: 'blur(35px)',
                            backdropFilter: 'blur(8px)',
                        }}
                    />

                    {/* Subtle Grid Pattern */}
                    <div
                        className="absolute inset-0 opacity-[0.02]"
                        style={{
                            backgroundImage: `
                                linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
                            `,
                            backgroundSize: '60px 60px',
                        }}
                    />

                    {/* Light Rays */}
                    <motion.div
                        className="absolute top-0 left-1/2 w-px h-full bg-gradient-to-b from-transparent via-white/10 to-transparent"
                        animate={{
                            opacity: [0.1, 0.3, 0.1],
                            scaleY: [0.8, 1.1, 0.8],
                        }}
                        transition={{
                            duration: 12,
                            repeat: Infinity,
                            ease: "easeInOut",
                        }}
                    />
                </div>

                <div className="relative z-10 container mx-auto px-4 md:px-6 lg:px-8 py-16">
                    <div className="flex flex-col items-center space-y-8">
                        {/* Back to Top Button with Liquid Glass */}
                        <motion.button
                            onClick={scrollToTop}
                            className="relative p-4 rounded-full text-white group overflow-hidden"
                            style={{
                                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                                backdropFilter: 'blur(20px) brightness(1.1)',
                                WebkitBackdropFilter: 'blur(20px) brightness(1.1)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                boxShadow: `
                                    0 8px 32px rgba(0, 0, 0, 0.3),
                                    inset 0 1px 0 rgba(255, 255, 255, 0.2),
                                    inset 0 -1px 0 rgba(255, 255, 255, 0.1)
                                `,
                            }}
                            whileHover={{
                                scale: 1.1,
                                y: -8,
                                filter: 'brightness(1.2)',
                            }}
                            whileTap={{ scale: 0.95 }}
                            transition={{ duration: 0.3, ease: [0.19, 1, 0.22, 1] }}
                        >
                            {/* Shimmer Effect */}
                            <motion.div
                                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                                initial={{ x: '-100%' }}
                                whileHover={{ x: '100%' }}
                                transition={{ duration: 0.6 }}
                            />
                            <ChevronUp size={24} className="relative z-10" />
                        </motion.button>

                        {/* Footer Content with Glass Effect */}
                        <div className="text-center space-y-6">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6 }}
                                className="relative"
                            >
                                {/* Glass Card for Content */}
                                <div
                                    className="relative p-8 rounded-2xl"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.04) 100%)',
                                        backdropFilter: 'blur(15px) saturate(120%)',
                                        WebkitBackdropFilter: 'blur(15px) saturate(120%)',
                                        border: '1px solid rgba(255, 255, 255, 0.15)',
                                        boxShadow: `
                                            0 16px 40px rgba(0, 0, 0, 0.2),
                                            inset 0 1px 0 rgba(255, 255, 255, 0.1),
                                            inset 0 -1px 0 rgba(255, 255, 255, 0.05)
                                        `,
                                    }}
                                >
                                    <h3 className="text-2xl font-bold mb-2">
                                        <span
                                            className="bg-gradient-to-r from-sky-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent"
                                            style={{
                                                backgroundSize: '200% 200%',
                                                animation: 'gradient 3s ease infinite',
                                            }}
                                        >
                                            Yutaro Mizuno
                                        </span>
                                    </h3>
                                    <p className="text-slate-300 text-sm mb-4">
                                        Content Planner
                                    </p>

                                    {/* Decorative Line with Liquid Effect */}
                                    <motion.div
                                        initial={{ scaleX: 0 }}
                                        whileInView={{ scaleX: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: 0.3, duration: 0.8 }}
                                        className="w-24 h-px mx-auto mb-4"
                                        style={{
                                            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
                                            filter: 'blur(0.5px)',
                                        }}
                                    />

                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        whileInView={{ opacity: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: 0.5, duration: 0.6 }}
                                        className="text-sm text-slate-400"
                                    >
                                        <span>© {currentYear} Minemu All rights reserved</span>
                                    </motion.div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </div>

                {/* Bottom Fade */}
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-900 to-transparent" />
            </div>
        </footer>
    );
};

export default Footer;